// ============================================
// 🏆 랭킹 컴포넌트 - 최소 디자인 버전
// ============================================
import React, { useState, useEffect } from 'react';
import { db, auth, analytics, logEvent } from '../firebase';
import { collection, getDocs, query, where, orderBy, limit, addDoc } from 'firebase/firestore';

function Ranking() {
  const [rankings, setRankings] = useState([]);
  const [filteredRankings, setFilteredRankings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [myRanking, setMyRanking] = useState(null);
  const [myGroups, setMyGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [viewMode, setViewMode] = useState('overall');

  // 프로필 모달
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userStats, setUserStats] = useState(null);
  const [recentRecords, setRecentRecords] = useState([]);

  // 신고 모달
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportTarget, setReportTarget] = useState(null);
  const [reportReason, setReportReason] = useState('');
  const [reportContent, setReportContent] = useState('');
  const [submittingReport, setSubmittingReport] = useState(false);

  useEffect(() => {
    loadMyGroups();
    loadRankings();
  }, []);

  useEffect(() => {
    applyFilter();
  }, [filter, rankings, selectedGroup, viewMode]);

  const loadMyGroups = async () => {
    try {
      const groupsSnapshot = await getDocs(collection(db, 'groups'));
      const allGroups = groupsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      const myGroupsList = allGroups.filter(group => 
        group.members && group.members.includes(auth.currentUser.uid)
      );

      setMyGroups(myGroupsList);
    } catch (error) {
      console.error('그룹 로드 실패:', error);
    }
  };

  const loadRankings = async () => {
    setLoading(true);
    try {
      // 먼저 모든 users 가져오기
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const usersMap = {};
      usersSnapshot.docs.forEach(doc => {
        usersMap[doc.id] = doc.data();
      });

      // user_records에서 통계 계산
      const recordsSnapshot = await getDocs(collection(db, 'user_records'));
      const records = recordsSnapshot.docs.map(doc => doc.data());

      const userStatsMap = {};

      records.forEach(record => {
        const userId = record.userId;
        if (!userStatsMap[userId]) {
          userStatsMap[userId] = {
            totalSteps: 0,
            recordCount: 0,
            uniqueStairs: new Set()
          };
        }

        userStatsMap[userId].totalSteps += record.steps || 0;
        userStatsMap[userId].recordCount += 1;
        userStatsMap[userId].uniqueStairs.add(record.stairId);
      });

      // 모든 users를 랭킹에 포함 (기록이 없어도 표시)
      const rankingsArray = Object.keys(usersMap).map(userId => {
        const stats = userStatsMap[userId] || {
          totalSteps: 0,
          recordCount: 0,
          uniqueStairs: new Set()
        };

        return {
          userId: userId,
          userEmail: usersMap[userId]?.email || '',
          totalSteps: stats.totalSteps,
          recordCount: stats.recordCount,
          uniqueStairs: stats.uniqueStairs.size || 0,
          nickname: usersMap[userId]?.nickname || '익명',
          region: usersMap[userId]?.region || '미설정',
          photoURL: usersMap[userId]?.photoURL || null
        };
      });

      rankingsArray.sort((a, b) => b.totalSteps - a.totalSteps);

      rankingsArray.forEach((user, index) => {
        user.rank = index + 1;
      });

      setRankings(rankingsArray);

      const myRank = rankingsArray.find(r => r.userId === auth.currentUser.uid);
      setMyRanking(myRank);

    } catch (error) {
      console.error('랭킹 로드 실패:', error);
    }
    setLoading(false);
  };

  const applyFilter = () => {
    let filtered = [...rankings];

    if (viewMode === 'group' && selectedGroup) {
      filtered = filtered.filter(user =>
        selectedGroup.members && selectedGroup.members.includes(user.userId)
      );

      filtered.forEach((user, index) => {
        user.rank = index + 1;
      });
    }

    if (filter !== 'all') {
      filtered = filtered.filter(user => user.region === filter);
      logEvent(analytics, 'filter_ranking', {
        filter_type: 'region',
        region: filter
      });
    }

    setFilteredRankings(filtered);
  };

  const handleSelectGroup = (group) => {
    logEvent(analytics, 'select_group_ranking', {
      group_id: group.id,
      group_name: group.name
    });
    setSelectedGroup(group);
    setViewMode('group');
  };

  // 사용자 프로필 보기
  const handleUserClick = async (user) => {
    if (user.userId === auth.currentUser.uid) {
      // 본인은 클릭해도 아무 동작 없음
      return;
    }

    try {
      logEvent(analytics, 'view_user_profile', {
        user_id: user.userId,
        rank: user.rank
      });
      setSelectedUser(user);
      
      const recordsQuery = query(
        collection(db, 'user_records'),
        where('userId', '==', user.userId),
        orderBy('timestamp', 'desc'),
        limit(5)
      );
      
      const recordsSnapshot = await getDocs(recordsQuery);
      const records = recordsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayRecords = records.filter(r => {
        const recordDate = r.timestamp.toDate();
        recordDate.setHours(0, 0, 0, 0);
        return recordDate.getTime() === today.getTime();
      });
      const todaySteps = todayRecords.reduce((sum, r) => sum + (r.steps || 0), 0);

      setUserStats({
        todaySteps,
        totalSteps: user.totalSteps,
        recordCount: user.recordCount,
        uniqueStairs: user.uniqueStairs
      });
      
      setRecentRecords(records);
      setShowProfileModal(true);

    } catch (error) {
      console.error('사용자 정보 로드 실패:', error);
    }
  };

  const closeProfileModal = () => {
    setShowProfileModal(false);
    setSelectedUser(null);
    setUserStats(null);
    setRecentRecords([]);
  };

  // 신고하기
  const handleReport = (user) => {
    logEvent(analytics, 'click_report_user', {
      reported_user_id: user.userId
    });
    setReportTarget(user);
    setShowReportModal(true);
  };

  const submitReport = async () => {
    if (!reportReason || !reportContent.trim()) {
      alert('신고 사유와 내용을 모두 입력해주세요');
      return;
    }

    setSubmittingReport(true);

    try {
      await addDoc(collection(db, 'reports'), {
        reporterId: auth.currentUser.uid,
        reporterEmail: auth.currentUser.email,
        reportedUserId: reportTarget.userId,
        reportedUserEmail: reportTarget.userEmail,
        reportedUserNickname: reportTarget.nickname,
        reason: reportReason,
        content: reportContent,
        status: 'pending',
        createdAt: new Date(),
        updatedAt: new Date()
      });

      alert('✅ 신고가 접수되었습니다. 관리자가 검토 후 조치하겠습니다.');

      logEvent(analytics, 'submit_report', {
        reported_user_id: reportTarget.userId,
        reason: reportReason
      });

      setShowReportModal(false);
      setShowProfileModal(false);
      setReportTarget(null);
      setReportReason('');
      setReportContent('');

    } catch (error) {
      alert('❌ 신고 접수 실패: ' + error.message);
      console.error(error);
    }

    setSubmittingReport(false);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const hours = date.getHours();
    const minutes = date.getMinutes();
    return `${month}/${day} ${hours}:${minutes < 10 ? '0' : ''}${minutes}`;
  };

  // 지역별 사용자 수 계산 및 정렬
  const getSortedRegions = () => {
    // 지역별 사용자 수 카운트
    const regionCount = {};
    rankings.forEach(user => {
      const region = user.region;
      if (region && region !== '미설정') {
        regionCount[region] = (regionCount[region] || 0) + 1;
      }
    });

    // 사용자 수가 많은 순서로 정렬
    const sortedRegions = Object.keys(regionCount).sort((a, b) => {
      return regionCount[b] - regionCount[a];
    });

    // 내 지역을 맨 앞으로
    if (myRanking && myRanking.region && myRanking.region !== '미설정') {
      const myRegion = myRanking.region;
      const filtered = sortedRegions.filter(r => r !== myRegion);
      return [myRegion, ...filtered];
    }

    return sortedRegions;
  };

  const regions = getSortedRegions();


  if (loading) {
    return (
      <div style={styles.container}>
        <h2 style={styles.title}>🏆 랭킹</h2>
        <div style={styles.loading}>로딩 중...</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>★ 랭킹</h2>

      {/* 모드 선택 */}
      <div style={styles.modeSelector}>
        <button
          onClick={() => {
            setViewMode('overall');
            setSelectedGroup(null);
          }}
          style={{
            ...styles.modeButton,
            ...(viewMode === 'overall' ? styles.modeButtonActive : {})
          }}
        >
          전체 랭킹
        </button>
        
        {myGroups.length > 0 && (
          <select
            onChange={(e) => {
              const group = myGroups.find(g => g.id === e.target.value);
              if (group) {
                handleSelectGroup(group);
              }
            }}
            style={styles.groupSelect}
            value={selectedGroup?.id || ''}
          >
            <option value="">그룹 선택</option>
            {myGroups.map(group => (
              <option key={group.id} value={group.id}>
                {group.name}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* 그룹 랭킹 배너 */}
      {viewMode === 'group' && selectedGroup && (
        <div style={styles.groupBanner}>
          <div>{selectedGroup.name} 그룹 랭킹</div>
          <div>멤버 {selectedGroup.members?.length || 0}명</div>
        </div>
      )}

      {/* 지역 필터 */}
      <div style={styles.filterContainer}>
        <button
          onClick={() => setFilter('all')}
          style={filter === 'all' ? styles.filterButtonActive : styles.filterButton}
        >
          전체
        </button>
        {regions.map(region => (
          <button
            key={region}
            onClick={() => setFilter(region)}
            style={filter === region ? styles.filterButtonActive : styles.filterButton}
          >
            {region}
          </button>
        ))}
      </div>

      {/* 내 랭킹 */}
      {myRanking && (
        <div style={styles.myRankBox}>
          <div style={styles.myRankLabel}>내 순위</div>
          <div style={styles.myRankRow}>
            <div style={styles.rankNumber}>{myRanking.rank}위</div>
            <div style={styles.userInfo}>
              {myRanking.photoURL ? (
                <img src={myRanking.photoURL} alt="" style={styles.profilePhoto} />
              ) : (
                <div style={styles.profilePlaceholder}>👤</div>
              )}
              <div style={styles.userDetails}>
                <div style={styles.nickname}>{myRanking.nickname}</div>
                <div style={styles.stats}>
                  {myRanking.recordCount}회 · {myRanking.totalSteps.toLocaleString()}개
                </div>
              </div>
            </div>
            <div style={styles.region}>{myRanking.region}</div>
          </div>
        </div>
      )}

      {/* 전체/그룹 랭킹 리스트 */}
      <div style={styles.listHeader}>
        <span style={{ width: 40 }}>순위</span>
        <span style={{ flex: 1 }}>닉네임</span>
        <span style={{ width: 120, textAlign: 'right' }}>총 계단</span>
        <span style={{ width: 80, textAlign: 'right' }}>지역</span>
      </div>
      <div style={styles.rankingList}>
        {filteredRankings.map((user) => {
          const isMe = user.userId === auth.currentUser.uid;
          
          return (
            <div
              key={user.userId}
              style={{
                ...styles.rankRow,
                ...(isMe ? styles.myRankHighlight : {}),
                cursor: isMe ? 'default' : 'pointer'
              }}
              onClick={() => handleUserClick(user)}
            >
              <div style={styles.rankNumber}>
                {user.rank <= 3 ? ['①','②','③'][user.rank - 1] : `${user.rank}`}
              </div>
              
              <div style={styles.userInfo}>
                {user.photoURL ? (
                  <img src={user.photoURL} alt="" style={styles.profilePhoto} />
                ) : (
                  <div style={styles.profilePlaceholder}>👤</div>
                )}
                <div style={styles.userDetails}>
                  <div style={styles.nickname}>
                    {user.nickname}
                    {isMe && <span style={styles.meBadge}> (나)</span>}
                  </div>
                  <div style={styles.stats}>
                    {user.recordCount}회 · {user.totalSteps.toLocaleString()}개
                  </div>
                </div>
              </div>
              
              <div style={styles.region}>{user.region}</div>
            </div>
          );
        })}
      </div>

      {/* 사용자 프로필 모달 */}
      {showProfileModal && selectedUser && (
        <div style={styles.modalOverlay} onClick={closeProfileModal}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <button onClick={closeProfileModal} style={styles.closeButton}>✕</button>
            
            <div style={styles.modalHeader}>
              {selectedUser.photoURL ? (
                <img src={selectedUser.photoURL} alt="" style={styles.modalProfilePhoto} />
              ) : (
                <div style={styles.modalProfilePlaceholder}>👤</div>
              )}
              <div>
                <div style={styles.modalNickname}>{selectedUser.nickname}</div>
                <div style={styles.modalRegion}>{selectedUser.region}</div>
              </div>
            </div>

            <div style={styles.modalStats}>
              <div style={styles.statBox}>
                <div style={styles.statValue}>{userStats?.todaySteps.toLocaleString()}</div>
                <div style={styles.statLabel}>오늘</div>
              </div>
              <div style={styles.statBox}>
                <div style={styles.statValue}>{userStats?.totalSteps.toLocaleString()}</div>
                <div style={styles.statLabel}>총 계단</div>
              </div>
              <div style={styles.statBox}>
                <div style={styles.statValue}>{userStats?.recordCount}</div>
                <div style={styles.statLabel}>기록 횟수</div>
              </div>
              <div style={styles.statBox}>
                <div style={styles.statValue}>{userStats?.uniqueStairs}</div>
                <div style={styles.statLabel}>방문 계단</div>
              </div>
            </div>

            {recentRecords.length > 0 && (
              <div style={styles.recentSection}>
                <div style={styles.recentTitle}>최근 기록</div>
                <div style={styles.recentList}>
                  {recentRecords.map((record, index) => (
                    <div key={index} style={styles.recentItem}>
                      <div>{record.stairName}</div>
                      <div style={styles.recentInfo}>
                        {record.steps}개 · {formatDate(record.timestamp)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={styles.reportSection}>
              <button
                onClick={() => handleReport(selectedUser)}
                style={styles.reportButton}
              >
                이 사용자 신고하기
              </button>
              <p style={styles.reportHint}>
                부적절한 닉네임, 사진, 허위 기록 등을 신고해주세요.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* 신고 모달 */}
      {showReportModal && reportTarget && (
        <div style={styles.modalOverlay} onClick={() => setShowReportModal(false)}>
          <div style={styles.reportModal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.reportModalTitle}>사용자 신고</div>
            
            <div style={styles.reportInfo}>
              신고 대상: <strong>{reportTarget.nickname}</strong>
            </div>

            <div style={styles.field}>
              <label style={styles.label}>신고 사유 *</label>
              <select
                value={reportReason}
                onChange={(e) => setReportReason(e.target.value)}
                style={styles.select}
              >
                <option value="">선택하세요</option>
                <option value="inappropriate_nickname">부적절한 닉네임</option>
                <option value="inappropriate_photo">부적절한 프로필 사진</option>
                <option value="fake_records">허위 기록</option>
                <option value="spam">스팸/광고</option>
                <option value="harassment">괴롭힘</option>
                <option value="other">기타</option>
              </select>
            </div>

            <div style={styles.field}>
              <label style={styles.label}>상세 내용 *</label>
              <textarea
                value={reportContent}
                onChange={(e) => setReportContent(e.target.value)}
                placeholder="신고 사유를 자세히 작성해주세요."
                style={styles.textarea}
                maxLength={500}
              />
              <div style={styles.charCount}>
                {reportContent.length} / 500
              </div>
            </div>

            <div style={styles.reportActions}>
              <button
                onClick={() => {
                  setShowReportModal(false);
                  setReportReason('');
                  setReportContent('');
                }}
                style={styles.reportCancel}
              >
                취소
              </button>
              <button
                onClick={submitReport}
                style={styles.reportSubmit}
                disabled={!reportReason || !reportContent.trim() || submittingReport}
              >
                {submittingReport ? '제출 중...' : '신고하기'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// 🎨 최소 스타일
// ============================================
const styles = {
  container: {
    maxWidth: '600px',
    margin: '0 auto',
    padding: '16px',
    fontSize: '14px',
    lineHeight: 1.4
  },
  title: {
    fontSize: '16px',
    fontWeight: '600',
    marginBottom: '12px'
  },
  loading: {
    padding: '20px 0'
  },

  // 모드 선택
  modeSelector: {
    display: 'flex',
    gap: '8px',
    marginBottom: '12px'
  },
  modeButton: {
    flex: 1,
    padding: '6px 8px',
    fontSize: '13px',
    backgroundColor: 'transparent',
    border: '1px solid #ddd',
    cursor: 'pointer'
  },
  modeButtonActive: {
    backgroundColor: '#eee',
    fontWeight: '600'
  },
  groupSelect: {
    flex: 1,
    padding: '6px 8px',
    fontSize: '13px',
    border: '1px solid #ddd'
  },

  groupBanner: {
    padding: '8px',
    border: '1px solid #ddd',
    marginBottom: '12px',
    fontSize: '13px'
  },

  // 지역 필터
  filterContainer: {
    display: 'flex',
    gap: '8px',
    marginBottom: '12px',
    flexWrap: 'wrap'
  },
  filterButton: {
    padding: '4px 8px',
    fontSize: '12px',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: '#555'
  },
  filterButtonActive: {
    padding: '4px 8px',
    fontSize: '12px',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontWeight: '600',
    textDecoration: 'underline',
    color: '#000'
  },

  // 내 랭킹
  myRankBox: {
    padding: '8px 0',
    borderTop: '1px solid #ddd',
    borderBottom: '1px solid #ddd',
    marginBottom: '8px'
  },
  myRankLabel: {
    fontSize: '12px',
    color: '#555',
    marginBottom: '4px'
  },
  myRankRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },

  // 리스트 헤더 & 행
  listHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: '6px 0',
    borderBottom: '1px solid #ddd',
    fontSize: '12px',
    color: '#555'
  },
  rankingList: {
    display: 'flex',
    flexDirection: 'column'
  },
  rankRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '8px 0',
    borderBottom: '1px solid #eee'
  },
  myRankHighlight: {
    backgroundColor: '#fafafa'
  },
  rankNumber: {
    width: 40,
    fontSize: '13px',
    textAlign: 'center'
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flex: 1
  },
  profilePhoto: {
    width: '32px',
    height: '32px',
    objectFit: 'cover',
    border: '1px solid #ddd'
  },
  profilePlaceholder: {
    width: '32px',
    height: '32px',
    backgroundColor: '#eee',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px',
    border: '1px solid #ddd'
  },
  userDetails: {
    flex: 1,
    minWidth: 0
  },
  nickname: {
    fontSize: '13px',
    fontWeight: '500',
    marginBottom: '2px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  meBadge: {
    fontSize: '11px'
  },
  stats: {
    fontSize: '12px',
    color: '#555'
  },
  region: {
    width: 80,
    textAlign: 'right',
    fontSize: '12px',
    color: '#555'
  },

  // 모달 공통
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.3)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modal: {
    backgroundColor: '#fff',
    border: '1px solid #ccc',
    width: '90%',
    maxWidth: '380px',
    maxHeight: '80vh',
    overflow: 'auto',
    padding: '12px',
    position: 'relative',
    fontSize: '13px'
  },
  closeButton: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: '16px',
    padding: 0
  },

  // 프로필 모달
  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    paddingBottom: '8px',
    borderBottom: '1px solid #eee',
    marginBottom: '8px'
  },
  modalProfilePhoto: {
    width: '40px',
    height: '40px',
    objectFit: 'cover',
    border: '1px solid #ddd'
  },
  modalProfilePlaceholder: {
    width: '40px',
    height: '40px',
    backgroundColor: '#eee',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid #ddd'
  },
  modalNickname: {
    fontSize: '14px',
    fontWeight: '600'
  },
  modalRegion: {
    fontSize: '12px',
    color: '#555'
  },
  modalStats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '8px',
    padding: '8px 0',
    borderBottom: '1px solid #eee',
    marginBottom: '8px'
  },
  statBox: {
    textAlign: 'center'
  },
  statValue: {
    fontSize: '13px',
    fontWeight: '600',
    marginBottom: '2px'
  },
  statLabel: {
    fontSize: '11px',
    color: '#555'
  },

  recentSection: {
    padding: '4px 0 8px'
  },
  recentTitle: {
    fontSize: '13px',
    fontWeight: '600',
    marginBottom: '4px'
  },
  recentList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  recentItem: {
    padding: '4px 0',
    borderBottom: '1px solid #f0f0f0'
  },
  recentInfo: {
    fontSize: '11px',
    color: '#555'
  },

  // 신고 섹션
  reportSection: {
    paddingTop: '8px',
    borderTop: '1px solid #eee'
  },
  reportButton: {
    width: '100%',
    padding: '6px 0',
    border: '1px solid #c62828',
    backgroundColor: 'transparent',
    color: '#c62828',
    fontSize: '12px',
    cursor: 'pointer',
    marginBottom: '4px'
  },
  reportHint: {
    fontSize: '11px',
    color: '#555',
    margin: 0
  },

  // 신고 모달
  reportModal: {
    backgroundColor: '#fff',
    border: '1px solid #ccc',
    width: '90%',
    maxWidth: '380px',
    padding: '12px',
    fontSize: '13px'
  },
  reportModalTitle: {
    fontSize: '14px',
    fontWeight: '600',
    marginBottom: '8px'
  },
  reportInfo: {
    padding: '6px 8px',
    border: '1px solid #eee',
    fontSize: '12px',
    marginBottom: '10px'
  },
  field: {
    marginBottom: '10px'
  },
  label: {
    display: 'block',
    marginBottom: '4px',
    fontSize: '12px'
  },
  select: {
    width: '100%',
    padding: '6px 8px',
    fontSize: '13px',
    border: '1px solid #ddd',
    boxSizing: 'border-box'
  },
  textarea: {
    width: '100%',
    padding: '6px 8px',
    fontSize: '13px',
    border: '1px solid #ddd',
    boxSizing: 'border-box',
    minHeight: '80px',
    resize: 'vertical',
    fontFamily: 'inherit'
  },
  charCount: {
    textAlign: 'right',
    fontSize: '11px',
    color: '#777'
  },
  reportActions: {
    display: 'flex',
    gap: '8px',
    marginTop: '8px'
  },
  reportCancel: {
    flex: 1,
    padding: '6px 0',
    backgroundColor: '#eee',
    border: '1px solid #ddd',
    fontSize: '12px',
    cursor: 'pointer'
  },
  reportSubmit: {
    flex: 1,
    padding: '6px 0',
    backgroundColor: '#c62828',
    border: '1px solid #c62828',
    color: '#fff',
    fontSize: '12px',
    cursor: 'pointer'
  }
};

export default Ranking;
